import {
  ArrowDown,
  ArrowUp,
  ArrowRight,
  Coins,
  Minus,
  Plus,
  ArrowRightLeft,
  RefreshCw,
  Scale,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAvatar } from "@/shared/components/data-display/ColocaAvatar";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaMerchantMark } from "@/shared/components/data-display/ColocaMerchantMark";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { merchantBrandOf } from "@/shared/utils/merchantBrands";

import type { CardAugmented } from "../constants/cardMocks.constants";
import { useCardStats } from "../hooks/useCardStats";
import { CardsService } from "../services/cardsService";
import type { CardTransaction } from "../types/card-movements.types";
import type { CardStatsRange } from "../types/card-stats.types";
import { formatCardBalance } from "../utils/walletMovementFormat";
import { CardRecentMovements } from "./CardRecentMovements";
import { CardUsageChart } from "./CardUsageChart";
import { PeriodFilter } from "./PeriodFilter";

type CardDetailOverviewTabProps = {
  card: CardAugmented;
  refreshKey?: number;
  onTopUp?: () => void;
  onMove?: () => void;
  /** Only when the card's organization has the card wallet to withdraw to. */
  onWithdraw?: () => void;
  /** The card takes crypto deposits right now: a quiet row opens the load dialog on its address. */
  onReceiveCrypto?: () => void;
  movementsDisabled?: boolean;
  /** The page's first page of movements; the overview shows the latest three. */
  recentMovements: CardTransaction[];
  isLoadingMovements?: boolean;
  onViewAllMovements: () => void;
  onMovementClick?: (transactionId: string) => void;
  /** Movements that just arrived by socket. */
  freshMovementIds?: ReadonlySet<string>;
};

const formatUSD = (raw: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(raw);

type BalanceStatus = {
  variant: "success" | "warning" | "destructive" | "secondary";
  labelKey:
    | "card_detail_balance_status_healthy"
    | "card_detail_balance_status_attention"
    | "card_detail_balance_status_low"
    | "card_detail_balance_status_empty"
    | "card_detail_balance_status_frozen";
};

function deriveBalanceStatus(balance: number, dailyRate: number, isFrozen: boolean): BalanceStatus {
  if (isFrozen)
    return {
      variant: "secondary",
      labelKey: "card_detail_balance_status_frozen",
    };
  if (balance === 0)
    return {
      variant: "destructive",
      labelKey: "card_detail_balance_status_empty",
    };
  if (dailyRate <= 0)
    return {
      variant: "success",
      labelKey: "card_detail_balance_status_healthy",
    };
  const runway = balance / dailyRate;
  if (runway < 7)
    return {
      variant: "destructive",
      labelKey: "card_detail_balance_status_low",
    };
  if (runway < 21)
    return {
      variant: "warning",
      labelKey: "card_detail_balance_status_attention",
    };
  return { variant: "success", labelKey: "card_detail_balance_status_healthy" };
}

export const CardDetailOverviewTab = ({
  card,
  refreshKey,
  onTopUp,
  onMove,
  onWithdraw,
  onReceiveCrypto,
  movementsDisabled,
  recentMovements,
  isLoadingMovements,
  onViewAllMovements,
  onMovementClick,
  freshMovementIds,
}: CardDetailOverviewTabProps) => {
  const { t } = useTranslations("cards");
  const navigate = useNavigate();

  const [range, setRange] = useState<CardStatsRange>("30d");
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Full-precision balance from a manual refresh; overrides the 2-decimal stats.available_balance.
  const [refreshedBalance, setRefreshedBalance] = useState<number | null>(null);
  const { stats, isLoading, error, refresh } = useCardStats(card.id, range, refreshKey);

  // A refresh from the page (an operation done here, a purchase pushed by socket) brings a newer balance on the
  // card, so a figure read by hand earlier must stop hiding it.
  useEffect(() => setRefreshedBalance(null), [card.id, refreshKey]);

  const handleRefreshBalance = async () => {
    setIsRefreshing(true);
    try {
      const res = await CardsService.getCardBalance(card.id);
      const fresh = Number.parseFloat(res.data?.current?.balance ?? "");
      if (Number.isFinite(fresh)) setRefreshedBalance(fresh);
      await refresh();
    } catch {
      // ignore — keep the last known balance on a failed manual refresh
    } finally {
      setIsRefreshing(false);
    }
  };

  // Full precision from card.balanceMock; stats.available_balance is rounded to 2 decimals (last-resort fallback).
  const balance = refreshedBalance ?? card.balanceMock ?? stats?.available_balance ?? 0;
  const loaded = stats?.loaded ?? { amount: 0, count: 0 };
  const spent = stats?.spent ?? { amount: 0, count: 0 };
  const net = stats?.net ?? 0;
  const avgTicket = stats?.avg_ticket ?? 0;
  const declined = stats?.declined_count ?? 0;
  const uniqueMerchants = stats?.unique_merchants ?? 0;
  const spendSeries = stats?.spend_series ?? [];
  const topMerchants = stats?.top_merchants ?? [];
  const days = stats?.period.days ?? 30;
  const dailyRate = days > 0 ? spent.amount / days : 0;
  const runway = dailyRate > 0 ? Math.floor(balance / dailyRate) : null;

  const balanceStatus = deriveBalanceStatus(balance, dailyRate, card.frozen);

  const rangeLabel = t(`card_stats_range_${range}` as never);

  const heroSubtitle = card.frozen
    ? t("card_detail_balance_hero_frozen")
    : runway == null
      ? t("card_detail_balance_hero_no_spend")
      : t("card_detail_balance_hero_runway", {
          rate: formatUSD(dailyRate),
          days: runway,
        });

  const kpis: ReadonlyArray<{
    label: string;
    value: string;
    detail: string;
    icon: LucideIcon;
    iconClassName: string;
    valueClassName?: string;
  }> = [
    {
      label: t("card_detail_kpi_loaded"),
      value: loaded.amount > 0 ? `+${formatUSD(loaded.amount)}` : formatUSD(0),
      detail: t("card_detail_period_loaded_count", { count: loaded.count }),
      icon: ArrowDown,
      iconClassName: "bg-success-container text-success-container-foreground",
      valueClassName: "text-success-container-foreground",
    },
    {
      label: t("card_detail_kpi_spent"),
      value: spent.amount > 0 ? `−${formatUSD(spent.amount)}` : formatUSD(0),
      detail: t("card_detail_kpi_spent_detail", { count: spent.count, amount: formatUSD(avgTicket) }),
      icon: ArrowUp,
      iconClassName: "bg-destructive/10 text-destructive",
    },
    {
      label: t("card_detail_period_net"),
      value: `${net > 0 ? "+" : net < 0 ? "−" : ""}${formatUSD(Math.abs(net))}`,
      detail:
        net > 0
          ? t("card_detail_period_net_positive")
          : net < 0
            ? t("card_detail_period_net_negative")
            : t("card_detail_period_net_flat"),
      icon: Scale,
      iconClassName: "bg-muted text-muted-foreground",
      valueClassName:
        net < 0 ? "text-warning-container-foreground" : net === 0 ? "text-muted-foreground" : undefined,
    },
    {
      label: t("card_detail_overview_kpi_declined"),
      value: String(declined),
      detail: declined > 0 ? t("card_detail_kpi_declined_some") : t("card_detail_kpi_declined_none"),
      icon: XCircle,
      iconClassName: declined > 0 ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground",
      valueClassName: declined > 0 ? "text-destructive" : undefined,
    },
  ];

  const chartBucketLabel =
    stats?.period.bucket === "day"
      ? t("card_detail_chart_bucket_day")
      : stats?.period.bucket === "month"
        ? t("card_detail_chart_bucket_month")
        : t("card_detail_chart_bucket_week");

  return (
    <div className="space-y-5">
      {/* What the card holds today and what can be done with it. Not scoped by the period below. */}
      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {t("card_detail_balance_hero_label")}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-4xl font-semibold leading-none tabular-nums tracking-tight text-foreground">
                {formatCardBalance(balance)}
              </span>
              <ColocaIconButton
                icon={RefreshCw}
                size="xs"
                variant="ghost"
                loading={isRefreshing}
                onClick={handleRefreshBalance}
                aria-label={t("balance_refresh_btn")}
              />
            </div>
            <div className="mt-1.5 text-xs text-muted-foreground">{heroSubtitle}</div>
          </div>
          <ColocaBadge variant={balanceStatus.variant}>{t(balanceStatus.labelKey)}</ColocaBadge>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
          {card.pocketRef ? (
            // Pocket-linked cards share the pocket's pool — load/transfer
            // are pocket-level operations, not card-level.
            <ColocaButton
              variant="primary"
              size="sm"
              icon={ArrowRight}
              onClick={() => navigate(`/app/cards/pockets/${card.pocketRef!.id}`)}
            >
              {t("card_detail_action_go_to_pocket")}
            </ColocaButton>
          ) : (
            <>
              <ColocaButton
                variant="primary"
                size="sm"
                icon={Plus}
                onClick={onTopUp}
                disabled={movementsDisabled}
              >
                {t("card_detail_action_load")}
              </ColocaButton>
              <ColocaButton
                variant="default"
                size="sm"
                icon={ArrowRightLeft}
                onClick={onMove}
                disabled={movementsDisabled}
              >
                {t("card_detail_action_move")}
              </ColocaButton>
              {onWithdraw && (
                <ColocaButton
                  variant="default"
                  size="sm"
                  icon={Minus}
                  onClick={onWithdraw}
                  disabled={movementsDisabled}
                >
                  {t("card_detail_action_withdraw")}
                </ColocaButton>
              )}
            </>
          )}
          {/* The address now lives in "Cargar"; this is the shortcut to it, shown only while the card can take
              a deposit, so a card without one never shows a dead entry. */}
          {onReceiveCrypto && !card.pocketRef && (
            <ColocaButton
              variant="ghost"
              size="sm"
              icon={Coins}
              iconTrailing={ArrowRight}
              onClick={onReceiveCrypto}
              className="ml-auto text-muted-foreground hover:text-primary"
            >
              {t("card_detail_receive_crypto_title")}
            </ColocaButton>
          )}
        </div>
      </div>

      <CardRecentMovements
        movements={recentMovements}
        isLoading={isLoadingMovements}
        onViewAll={onViewAllMovements}
        onRowClick={onMovementClick}
        highlightedIds={freshMovementIds}
      />

      {/* Everything from here on answers to the period picked in this header — and only this. */}
      <section className="@container space-y-4 pt-1">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">{t("card_detail_activity_title")}</h2>
            <p className="text-xs text-muted-foreground">{t("card_detail_activity_subtitle")}</p>
          </div>
          {/* Disabled on the first read only: a refresh from a live push must not swallow a click on a period, and
              the stats hook keeps the last period picked whatever order the answers land in. */}
          <PeriodFilter value={range} onChange={setRange} disabled={isLoading && !stats} />
        </div>

        {/* Columns follow the width this column gets, not the viewport: next to the 380px card sidebar a wide
            screen still leaves too little room for four tiles. */}
        <div className="grid grid-cols-2 gap-3 @3xl:grid-cols-4">
          {kpis.map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className="rounded-2xl border border-border bg-card p-4 shadow-sm">
                <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  <span
                    className={cn(
                      "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
                      k.iconClassName,
                    )}
                  >
                    <Icon size={10} strokeWidth={2.4} />
                  </span>
                  {k.label}
                </div>
                <div
                  className={cn(
                    "mt-1.5 whitespace-nowrap font-mono text-xl font-semibold tabular-nums text-foreground @lg:text-2xl",
                    k.valueClassName,
                  )}
                >
                  {k.value}
                </div>
                <div className="text-[11px] text-muted-foreground">{k.detail}</div>
              </div>
            );
          })}
        </div>

        <CardUsageChart
          title={t("card_detail_chart_title")}
          subtitle={`${chartBucketLabel} · ${t("card_detail_chart_avg", { amount: formatUSD(dailyRate) })}`}
          data={spendSeries}
          bucket={stats?.period.bucket ?? "week"}
          isLoading={isLoading && !stats}
        />

        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-foreground">{t("card_detail_top_merchants_title")}</div>
            <span className="text-[11px] text-muted-foreground">
              {t("card_detail_top_merchants_subtitle", { count: uniqueMerchants, label: rangeLabel })}
            </span>
          </div>
          {topMerchants.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              {t("card_detail_top_merchants_empty")}
            </div>
          ) : (
            <div className="space-y-3">
              {topMerchants.map((m) => (
                <div key={m.name} className="flex items-center gap-3">
                  {merchantBrandOf(m.name) ? (
                    <ColocaMerchantMark merchant={m.name} className="size-8" />
                  ) : (
                    <ColocaAvatar name={m.name} size="default" shape="square" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground">{m.name}</span>
                      <span className="font-mono text-sm tabular-nums text-foreground">
                        {formatUSD(m.amount)}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="relative h-1 flex-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className="absolute inset-y-0 left-0 rounded-full bg-primary"
                          style={{ width: `${m.share}%` }}
                        />
                      </div>
                      <span className="w-24 truncate text-right font-mono text-[10px] text-muted-foreground">
                        {m.category ?? "—"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            {error}
          </div>
        )}
      </section>
    </div>
  );
};
